import { zustandStorage } from "@helpers/storage";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { ItemState, ItemStates } from "./types";

// Same-device cache of summative selections: it can lead the backend answer
// echo right after a test ends, so report/score readers prefer it and fall
// back to seeding from /sessions/{id}/items on a fresh device.
type PersistedItemState = { selectedChoiceIds: string[]; timeTaken?: number };
type PersistedSessionItemStates = Record<number, PersistedItemState>;

type State = {
	itemStatesBySession: Record<number, PersistedSessionItemStates>;
	saveTestItemStates: (numericId: number, itemStates?: ItemStates) => void;
	getTestItemStates: (numericId: number) => ItemStates | undefined;
};

const serialize = (itemStates?: ItemStates): PersistedSessionItemStates => {
	const serialized: PersistedSessionItemStates = {};
	if (!itemStates) {
		return serialized;
	}
	for (const [itemId, itemState] of itemStates) {
		serialized[itemId] = {
			selectedChoiceIds: itemState.selectedChoiceIds ?? [],
			...(typeof itemState.timeTaken === "number"
				? { timeTaken: itemState.timeTaken }
				: {}),
		};
	}
	return serialized;
};

const deserialize = (
	serialized?: PersistedSessionItemStates,
): ItemStates | undefined => {
	if (!serialized) {
		return undefined;
	}
	const itemStates = new Map<number, ItemState>();
	for (const [itemId, { selectedChoiceIds, timeTaken }] of Object.entries(
		serialized,
	)) {
		itemStates.set(Number(itemId), {
			selectedChoiceIds,
			scores: [],
			timeTaken,
		});
	}
	return itemStates;
};

export const useProethicaTestItemStatesStore = create<State>()(
	persist(
		(set, get) => ({
			itemStatesBySession: {},
			saveTestItemStates: (numericId, itemStates) =>
				set((state) => ({
					itemStatesBySession: {
						...state.itemStatesBySession,
						[numericId]: serialize(itemStates),
					},
				})),
			getTestItemStates: (numericId) =>
				deserialize(get().itemStatesBySession[numericId]),
		}),
		{
			name: "proethicaTestItemStatesStore",
			storage: createJSONStorage(() => zustandStorage),
		},
	),
);
